/**
 * AuthContext — Firebase Auth, exposed via a stable Session/User contract.
 *
 * Internals are Firebase Auth (post v2 migration to Cloud SQL Postgres +
 * Firebase).  The shape the context EXPOSES (``session.access_token``,
 * ``session.user.{id,email,app_metadata,user_metadata}``) is frozen for
 * compatibility with the ~dozens of consumer files that read those
 * fields — the shape was originally modeled on the supabase-js User type
 * that the codebase used to wrap, and renaming the fields would touch
 * every caller.  See ``ShimUser`` / ``ShimSession`` below for the exact
 * surface.
 *
 * Field mapping:
 *   - ``session.access_token``    → Firebase ID token (auto-refreshed by SDK)
 *   - ``session.user.id``         → Firebase UID (opaque string)
 *   - ``session.user.email``      → from Firebase user
 *   - ``signInWithGoogle/Facebook`` → Firebase OAuth popups
 *   - ``signInWithMagicLink``     → Firebase email link sign-in
 *   - ``signOut``                  → Firebase signOut + reset state
 */
import {
  createContext, useContext, useEffect, useState, ReactNode,
  useCallback, useRef, useMemo,
} from 'react'
import {
  onAuthStateChanged,
  signOut as fbSignOut,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  sendSignInLinkToEmail,
  type User as AuthUser,
} from 'firebase/auth'
import { useQueryClient } from '@tanstack/react-query'
import { firebaseAuth, setAuthTokenGetter, setCachedAuthToken, setUnauthorizedHandler, getAccountStatus, AccountStatus, tryParseSoftDeleteFromResponse, createDebug, isMobileWeb } from '@ponpon/api-client'
import type { JsonObject } from '../types/json'
const log = createDebug('dw:account-self-service:auth-context')

// ---------------------------------------------------------------------------
// Stable User/Session contract (consumers expect these field names).
// ---------------------------------------------------------------------------
// ShimUser exposes only the fields v2 console consumers actually read.
// Field names are frozen — they originated from the supabase-js User
// type that the codebase used to wrap pre-v2, and renaming would touch
// every consumer file.  Don't add an index signature: keeping the type
// closed catches typos at call sites.
export interface ShimUser {
  id: string
  email?: string
  app_metadata: JsonObject
  user_metadata: {
    full_name?: string | null
    display_name?: string | null
    avatar_url?: string | null
    role?: string | null
  }
  identities?: Array<{ provider: string }>
  aud: string
  created_at: string
}

export interface ShimSession {
  access_token: string
  user: ShimUser
}

function adaptAuthUser(authUser: AuthUser): ShimUser {
  const identities = authUser.providerData.map((p) => ({
    provider: p.providerId === 'password' ? 'email' : p.providerId,
  }))
  const photo = authUser.photoURL ?? null
  return {
    id: authUser.uid,
    email: authUser.email ?? undefined,
    app_metadata: { provider: identities[0]?.provider ?? 'firebase' },
    user_metadata: {
      full_name: authUser.displayName ?? null,
      display_name: authUser.displayName ?? null,
      avatar_url: photo,
      role: null,
    },
    identities,
    aud: 'authenticated',
    created_at: authUser.metadata.creationTime ?? new Date().toISOString(),
  }
}

interface AuthContextValue {
  session: ShimSession | null
  user: ShimUser | null
  isLoading: boolean
  isAuthenticated: boolean
  signInWithGoogle: () => Promise<void>
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  hasPasswordIdentity: boolean
  isSoftDeleted: boolean
  softDeleteStatus: AccountStatus | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

// Additive export so v2 (apps/ui_v2) can vendor v1 components that call
// useAuth() WITHOUT mounting the full v1 AuthProvider (whose Firebase
// init + setAuthTokenGetter side-effects collide with v2's own
// localhost-null-token shim). v1 itself never imports AuthContext;
// useAuth and AuthProvider remain the only API for v1 consumers.
export { AuthContext as _AuthContextForVendoring }

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authUser, setAuthUser] = useState<AuthUser | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [softDeleteStatus, setSoftDeleteStatus] = useState<AccountStatus | null>(null)
  const queryClient = useQueryClient()
  const previousUserIdRef = useRef<string | null>(null)

  // Async token getter — fetches a fresh Firebase ID token. The SDK
  // auto-refreshes when within 5 minutes of expiry; ``getIdToken(false)``
  // returns the cached one if still valid.
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const u = firebaseAuth.currentUser
    if (!u) return null
    try {
      return await u.getIdToken(/* forceRefresh */ false)
    } catch (err) {
      log('getIdToken failed %o', err)
      return null
    }
  }, [])

  // Collect the credential after a mobile redirect sign-in.
  //
  // signInWithRedirect navigates away and back, so the result is waiting on
  // return rather than resolving from the call that started it. Without this
  // the mobile flow completes at Google and then does nothing here.
  //
  // Errors are logged, not surfaced: on every ordinary load (no redirect in
  // flight) this resolves to null, so a thrown error here would break the app
  // for users who never touched Google sign-in.
  useEffect(() => {
    getRedirectResult(firebaseAuth).catch((err) => {
      log('getRedirectResult failed %o', err)
    })
  }, [])

  // Listen for sign-in / sign-out / token refresh events.
  useEffect(() => {
    const unsub = onAuthStateChanged(firebaseAuth, async (next) => {
      setAuthUser(next)
      if (next) {
        try {
          const tok = await next.getIdToken(false)
          setAccessToken(tok)
        } catch (err) {
          log('initial getIdToken failed %o', err)
          setAccessToken(null)
        }
        queryClient.invalidateQueries()
      } else {
        setAccessToken(null)
        setSoftDeleteStatus(null)
      }
      setIsLoading(false)
    })
    return () => unsub()
  }, [queryClient])

  // Wire the token getter into apiFetch so all requests carry the JWT.
  useEffect(() => {
    setAuthTokenGetter(getAccessToken)
  }, [getAccessToken])

  useEffect(() => {
    setCachedAuthToken(accessToken)
  }, [accessToken])

  // Soft-delete handling: a 403 with restore_required flips the state
  // and (the page itself decides whether to) redirect to /account/restore.
  useEffect(() => {
    setUnauthorizedHandler(async (res: Response) => {
      const payload = await tryParseSoftDeleteFromResponse(res)
      if (!payload) return
      log('403 with restore_required — flipping isSoftDeleted')
      setSoftDeleteStatus({
        soft_deleted: true,
        scheduled_purge_at: payload.scheduledPurgeAt,
        soft_deleted_at: null,
        days_remaining: null,
      })
      if (typeof window !== 'undefined' && window.location.pathname !== '/account/restore') {
        window.location.assign('/account/restore')
      }
    })
    return () => setUnauthorizedHandler(null)
  }, [])

  useEffect(() => {
    if (!authUser?.uid) {
      setSoftDeleteStatus(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const status = await getAccountStatus()
        if (cancelled) return
        log('account status fetched soft_deleted=%s', status.soft_deleted)
        setSoftDeleteStatus(status)
        if (status.soft_deleted && typeof window !== 'undefined') {
          if (window.location.pathname !== '/account/restore') {
            window.location.assign('/account/restore')
          }
        }
      } catch (err) {
        log('account status fetch failed %o', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [authUser?.uid])

  useEffect(() => {
    const currentUserId = authUser?.uid ?? null
    if (previousUserIdRef.current !== currentUserId && previousUserIdRef.current !== null) {
      queryClient.invalidateQueries()
    }
    previousUserIdRef.current = currentUserId
  }, [authUser?.uid, queryClient])

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider()
    if (isMobileWeb()) {
      // signInWithPopup does not work on mobile web: iOS Safari blocks the
      // popup silently and in-app browsers refuse to open one at all, so the
      // user taps the button and nothing happens. Redirect navigates the page
      // itself; the credential is collected by the getRedirectResult effect.
      // apps/ui_v2 has done this for a while — the console had not, so every
      // mobile visitor here hit that dead state.
      await signInWithRedirect(firebaseAuth, provider)
      return
    }
    await signInWithPopup(firebaseAuth, provider)
  }

  const signInWithMagicLink = async (email: string) => {
    try {
      const url =
        import.meta.env.VITE_AUTH_REDIRECT_URL ||
        (typeof window !== 'undefined' ? window.location.origin : '')
      await sendSignInLinkToEmail(firebaseAuth, email, {
        url,
        handleCodeInApp: true,
      })
      return { error: null }
    } catch (err) {
      return { error: err instanceof Error ? err : new Error(String(err)) }
    }
  }

  const signOut = async () => {
    await fbSignOut(firebaseAuth)
  }

  const shimUser: ShimUser | null = useMemo(
    () => (authUser ? adaptAuthUser(authUser) : null),
    [authUser],
  )

  const shimSession: ShimSession | null = useMemo(
    () => (shimUser && accessToken ? { access_token: accessToken, user: shimUser } : null),
    [shimUser, accessToken],
  )

  const hasPasswordIdentity = useMemo(() => {
    if (!shimUser?.identities) return false
    return shimUser.identities.some((i) => i.provider === 'email')
  }, [shimUser])

  const value: AuthContextValue = {
    session: shimSession,
    user: shimUser,
    isLoading,
    isAuthenticated: !!shimSession,
    signInWithGoogle,
    signInWithMagicLink,
    signOut,
    hasPasswordIdentity,
    isSoftDeleted: softDeleteStatus?.soft_deleted ?? false,
    softDeleteStatus,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
